/* Class boards (Treasuries, Govt. debt, Credit). Tiles and panel frames use
   the page chrome from terminal.css; league rows (after the dashboard's
   leader lists), the entries grid and the empty states are styled here with
   the page's color tokens. The grid scrolls inside its own frame so the page
   never scrolls sideways at phone width. */

.blurb {
  max-width: 96ch;
  margin: -6px 0 12px;
  color: var(--ink-muted);
  font-size: 0.72rem;
  line-height: 1.5;
}

.leagues {
  align-items: start;
  margin: 0 0 12px;
}

/* ---- league rows ---- */

.league {
  margin: 0;
  padding: 0;
  list-style: none;
}

.league li {
  display: grid;
  grid-template-columns: 2em minmax(0, 1fr) auto 4.5em;
  grid-template-areas:
    "rank name value share"
    "rank bar bar bar";
  align-items: baseline;
  gap: 2px 10px;
  padding: 5px 10px 6px;
  border-bottom: 1px solid #161613;
  font-size: 0.78rem;
}

.league li:last-child {
  border-bottom: none;
}

.rank {
  grid-area: rank;
  color: var(--ink-muted);
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.name {
  display: flex;
  grid-area: name;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}

.nameText {
  overflow: hidden;
  color: var(--signal);
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.entries {
  color: var(--ink-muted);
  font-size: 0.68rem;
  white-space: nowrap;
}

.value {
  grid-area: value;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.absentValue {
  composes: value;
  color: var(--ink-muted);
}

.share {
  grid-area: share;
  color: var(--ink-muted);
  font-size: 0.72rem;
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.bar {
  grid-area: bar;
  height: 3px;
  background: #161613;
}

.bar > span {
  display: block;
  height: 100%;
  background: var(--signal);
}

/* ---- entries grid ---- */

.grid {
  display: grid;
  gap: 8px;
  min-width: 0;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
}

.search,
.select {
  min-height: 32px;
  padding: 0 8px;
  border: 1px solid var(--line-strong);
  background: #0d0d0d;
  color: var(--ink);
  font: inherit;
  font-size: 0.78rem;
}

.search {
  flex: 1 1 12rem;
  min-width: 0;
  max-width: 24rem;
}

.search::placeholder {
  color: var(--ink-muted);
}

.search:focus,
.select:focus {
  border-color: var(--signal);
}

.select {
  padding-right: 26px;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%23a78bfa' stroke-width='1.6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;
  cursor: pointer;
}

.select option {
  background: #0d0d0d;
  color: var(--ink);
}

.matchCount {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-left: auto;
  color: var(--ink-muted);
  font-size: 0.78rem;
  font-variant-numeric: tabular-nums;
}

.clear {
  padding: 0;
  border: 0;
  background: none;
  color: var(--signal);
  cursor: pointer;
  font: inherit;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.scroll {
  width: 100%;
  min-width: 0;
  max-height: calc(100vh - 13rem);
  overflow: auto;
  overscroll-behavior-x: contain;
  border: 1px solid var(--line);
  background: var(--paper-raised);
}

.table {
  min-width: 100%;
  border-collapse: collapse;
  font-variant-numeric: tabular-nums;
}

.table th,
.table td {
  white-space: nowrap;
  text-align: right;
}

.table th[data-align="left"],
.table td[data-align="left"] {
  text-align: left;
}

.table td {
  padding: 4px 8px;
  border-bottom: 1px solid #161613;
  font-size: 0.78rem;
}

.table tbody tr:hover td {
  background: #170d2a;
}

.table thead th {
  position: sticky;
  top: 0;
  z-index: 5;
  padding: 0;
  border-bottom: 1px solid var(--line-strong);
  background: var(--head-fill);
  color: var(--signal);
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.sort,
.plainHead {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  width: 100%;
  min-height: 34px;
  padding: 4px 8px;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  white-space: nowrap;
}

.sort {
  cursor: pointer;
}

.table th[data-align="left"] .sort,
.table th[data-align="left"] .plainHead {
  justify-content: flex-start;
}

.sortSorted {
  composes: sort;
  color: var(--ink);
}

.sort:hover,
.sortSorted:hover {
  color: var(--ink);
}

.sortMark {
  flex: 0 0 auto;
  font-size: 0.85em;
  opacity: 0.6;
}

.sortSorted .sortMark {
  opacity: 1;
}

.token {
  color: var(--signal);
  font-weight: 700;
}

.tokenName {
  margin-left: 8px;
  color: var(--ink-muted);
  font-size: 0.9em;
}

/* The ticker as a button: opens the token panel (U-3), like the stock grids. */
.tokenButton {
  composes: token;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  font: inherit;
  font-weight: 700;
  text-align: left;
}

.tokenButton:hover {
  text-decoration: underline;
  text-underline-offset: 3px;
}

/* "2 issues": why figures are missing this slot; the list is in the tooltip. */
.problems {
  margin-left: 6px;
  padding: 0 4px;
  border: 1px solid var(--down);
  color: var(--down);
  font-size: 0.62rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  vertical-align: 1px;
  cursor: help;
}

/* The price in the fund's own currency, under the USD price. */
.native {
  display: block;
  color: var(--ink-muted);
  font-size: 0.78em;
}

.issuer {
  color: inherit;
  text-decoration: none;
}

.issuer:hover {
  text-decoration: underline;
  text-underline-offset: 3px;
}

.muted {
  color: var(--ink-muted);
}

.absent {
  color: var(--ink-muted);
}

.badge,
.badgeTvl {
  display: inline-block;
  margin-left: 6px;
  padding: 0 4px;
  border: 1px solid var(--line-strong);
  color: var(--ink-muted);
  font-size: 0.64rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  vertical-align: 1px;
}

.badgeTvl {
  border-color: var(--signal);
  color: var(--signal);
}

.sub {
  margin-left: 5px;
  color: var(--ink-muted);
  font-size: 0.64rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.verified,
.unverified {
  font-size: 0.7rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.verified {
  color: var(--up);
}

.unverified {
  color: var(--ink-muted);
}

.links {
  display: inline-flex;
  gap: 10px;
}

.links a {
  color: var(--signal);
  font-size: 0.72rem;
  text-underline-offset: 3px;
}

.hasNote td {
  border-bottom: none;
}

.noteRow td {
  padding: 0 8px 6px 26px;
  border-bottom: 1px solid #161613;
  color: var(--ink-muted);
  font-size: 0.7rem;
  text-align: left;
  white-space: normal;
}

.noteRow td::before {
  content: "↳ ";
  color: var(--signal);
}

.table tbody .noteRow:hover td {
  background: transparent;
}

.emptyRow {
  padding: 14px 10px;
  color: var(--ink-muted);
  text-align: left;
}

.up {
  color: var(--up);
}

.down {
  color: var(--down);
}

/* ---- states ---- */

.state {
  max-width: 76ch;
  margin: 0 0 12px;
  padding: 14px 12px 16px;
  border: 1px solid var(--line);
  background: var(--paper-raised);
}

.stateTitle {
  margin: 0 0 6px;
  color: var(--signal);
  font-size: 0.78rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.state p {
  margin: 0;
  color: var(--ink-muted);
  font-size: 0.82rem;
  line-height: 1.55;
  text-wrap: pretty;
}

.state code {
  padding: 0 3px;
  border: 1px solid var(--line);
  background: var(--paper);
  color: var(--ink);
  font-size: 0.92em;
  overflow-wrap: anywhere;
}

@media (max-width: 480px) {
  .league li {
    grid-template-columns: 1.5em minmax(0, 1fr) auto 4em;
    gap: 2px 7px;
    padding-inline: 7px;
  }

  .entries {
    display: none;
  }

  .search {
    flex-basis: 100%;
    max-width: none;
  }

  .matchCount {
    margin-left: 0;
  }
}

/* Commodity boards (U-21): an ETF tracker row, valued under Stocks, and a token's unit. */
.tracker {
  display: inline-block;
  margin-left: 6px;
  padding: 0 5px;
  border: 1px solid var(--line-strong);
  border-radius: 4px;
  color: var(--ink-muted);
  font-size: 0.6rem;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.unit {
  display: block;
  color: var(--ink-muted);
  font-family: var(--mono);
  font-size: 0.62rem;
}

/* Private equity (U-22): what the holder owns, and row caveats. */
.ownFund,
.ownNotShares {
  display: inline-block;
  margin-top: 3px;
  padding: 1px 6px;
  border: 1px solid var(--line-strong);
  border-radius: 4px;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.ownFund {
  color: var(--ink);
}

/* Not company shares: the one thing a reader must not miss. */
.ownNotShares {
  border-color: var(--caution-line);
  background: var(--caution-fill);
  color: var(--caution);
}

.caveat {
  display: block;
  max-width: 26em;
  margin-top: 3px;
  color: var(--caution);
  font-size: 0.62rem;
  line-height: 1.35;
  white-space: normal;
}

/* Credit groups (U-24): one button per group with its total and entry count, as rwa.xyz groups credit. */
.groups {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
}

.group {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  padding: 6px 10px;
  border: 1px solid var(--line);
  border-radius: 6px;
  background: var(--paper-raised);
  color: var(--ink-muted);
  font-size: 0.7rem;
  font-weight: 700;
  cursor: pointer;
}

.group:hover {
  color: var(--ink);
}

.group:focus-visible {
  outline: 1px solid var(--signal);
  outline-offset: 1px;
}

.group[aria-pressed="true"] {
  border-color: var(--signal-fill);
  background: #272033;
  color: #d2bcff;
}

.groupFigure {
  font-family: var(--mono);
  font-size: 0.62rem;
  font-weight: 400;
}

.instrument {
  display: block;
  color: var(--ink-muted);
  font-size: 0.62rem;
}

/* The rest of a long registry note, on request. */
.noteMore {
  display: inline;
}

.noteMore summary {
  display: inline;
  margin-left: 4px;
  color: var(--signal);
  cursor: pointer;
  list-style: none;
}

.noteMore summary::-webkit-details-marker {
  display: none;
}

.noteMore[open] {
  display: block;
  margin-top: 4px;
}

/* CSV export of what the grid shows (C-7). */
.csv {
  min-height: 32px;
  padding: 0 10px;
  border: 1px solid var(--line-strong);
  background: transparent;
  color: var(--signal);
  cursor: pointer;
  font: inherit;
  font-size: 0.7rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.csv:disabled {
  color: var(--ink-muted);
  cursor: default;
}

/* DQ-12b: a represented entry, listed and valued but outside every total. */
.represented {
  display: inline-block;
  margin-left: 6px;
  padding: 0 5px;
  border: 1px solid var(--caution-line);
  border-radius: 4px;
  background: var(--caution-fill);
  color: var(--caution);
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  white-space: nowrap;
}
